import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { api } from "@/lib/api";
import { Composer } from "@/components/studio/chat/Composer";

const ProjectCard = ({ p }) => (
  <Link to={`/admin/studio/${p.id}`} data-testid={`studio-recent-${p.id}`}
    className="group rounded-2xl border border-white/5 bg-white/[0.02] p-5 transition-colors hover:border-cyan-400/30 hover:bg-cyan-400/[0.03]">
    <div className="mb-4 h-24 rounded-xl bg-[radial-gradient(circle_at_20%_20%,rgba(34,211,238,0.18),transparent_60%),linear-gradient(135deg,#0E1422,#07090E)]" />
    <p className="truncate font-semibold text-white group-hover:text-cyan-100">{p.name}</p>
    <p className="mt-1 text-xs text-slate-500">{p.current_version ? `v${p.current_version}` : p.status} · {formatDistanceToNow(parseISO(p.created_at), { addSuffix: true })}</p>
  </Link>
);

export default function StudioHome() {
  const nav = useNavigate();
  const [projects, setProjects] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientProject, setClientProject] = useState("");
  const [pending, setPending] = useState(null);
  useEffect(() => {
    api.get("/admin/studio/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
    api.get("/projects").then((r) => setClients(r.data)).catch(() => {});
  }, []);
  // The project is created on first upload or send, so attachments have somewhere to live.
  const ensure = async () => {
    if (pending) return pending;
    const p = (await api.post("/admin/studio/chat/projects", { client_project_id: clientProject || null })).data;
    setPending(p);
    return p;
  };
  const upload = async (file) => {
    const p = await ensure();
    const fd = new FormData();
    fd.append("file", file);
    return (await api.post(`/admin/studio/projects/${p.id}/attachments`, fd)).data;
  };
  const send = async (text, images) => {
    const p = await ensure();
    try {
      await api.post(`/admin/studio/projects/${p.id}/chat`, { text, images });
    } catch (e) {  // don't leave an empty orphan project behind
      await api.delete(`/admin/studio/projects/${p.id}`).catch(() => {});
      setPending(null);
      throw e;
    }
    nav(`/admin/studio/${p.id}`);
  };
  return (
    <div data-testid="studio-home" className="mx-auto max-w-5xl">
      <div className="pt-6 sm:pt-14">
        <p className="mb-3 inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.25em] text-cyan-300/80"><Sparkles className="h-3.5 w-3.5" />Beall Digital · AI Studio</p>
        <h1 className="font-display text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl">What should we build?</h1>
        <p className="mt-3 max-w-2xl text-base text-slate-400">Describe the site or app. Attach screenshots or a logo, or paste a link to a site you like — the agent asks a few questions, shows a plan, then builds it.</p>
        <div className="mt-8">
          <Composer big onSend={send} upload={upload} placeholder="A site for a yoga studio with class bookings…">
            <select value={clientProject} onChange={(e) => setClientProject(e.target.value)} disabled={!!pending} data-testid="studio-home-client-select"
              className="max-w-[12rem] truncate rounded-full border border-white/10 bg-transparent px-3 py-1.5 text-xs text-slate-300 outline-none">
              <option value="">No client project</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Composer>
        </div>
      </div>
      <section className="mt-14 pb-10">
        <h2 className="mb-4 text-base font-semibold text-slate-200 md:text-lg">Recent projects</h2>
        {projects === null ? <p className="text-sm text-slate-500">Loading…</p> : projects.length === 0 ? (
          <p data-testid="studio-recent-empty" className="text-sm text-slate-500">No projects yet — your first one starts above.</p>
        ) : (
          <div data-testid="studio-recent-grid" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{projects.filter((p) => p.name !== "New project" || p.current_version).map((p) => <ProjectCard key={p.id} p={p} />)}</div>
        )}
      </section>
    </div>
  );
}
